/**
 * White Label Landing Page - For Agencies
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Building2, CheckCircle, ArrowRight, Palette, Code, 
  Users, DollarSign, Shield, Zap
} from 'lucide-react';
import axios from 'axios';

const FEATURES = [
  {
    icon: Palette,
    title: "Your Branding",
    description: "Your logo, colors, and domain. Clients never see TripleTC."
  },
  {
    icon: Code,
    title: "Full API Access",
    description: "Integrate our AI into your existing tools and workflows."
  },
  {
    icon: Users,
    title: "Unlimited Users",
    description: "Add your whole team and all your clients. No per-seat fees."
  },
  {
    icon: DollarSign,
    title: "Set Your Prices",
    description: "Charge whatever you want. We charge you wholesale."
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "SOC 2 compliant. Data encryption. Your clients' data stays safe."
  },
  {
    icon: Zap,
    title: "Instant Setup",
    description: "Go live in under an hour. No development required."
  }
];

export default function WhiteLabel() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    // In production, this would save to database
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero */}
      <section className="pt-24 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 mb-8">
              <Building2 className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-300">
                White Label Program
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Your Brand.<br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Our AI.
              </span>
            </h1>

            <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
              Offer AI services to your clients under your own brand.
              We power it. You profit from it.
            </p>

            {/* Pricing Preview */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <div className="bg-zinc-900/50 border border-zinc-700 rounded-xl px-6 py-4">
                <div className="text-zinc-400 text-sm">Starting at</div>
                <div className="text-3xl font-black text-white">$299<span className="text-lg text-zinc-500">/mo</span></div>
              </div>
              <div className="text-zinc-500">or</div>
              <div className="bg-zinc-900/50 border border-zinc-700 rounded-xl px-6 py-4">
                <div className="text-zinc-400 text-sm">Revenue share</div>
                <div className="text-3xl font-black text-white">20%<span className="text-lg text-zinc-500"> of usage</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Everything You Need to Resell AI
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6"
              >
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-400 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Perfect For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Marketing Agencies", desc: "Offer AI content creation as an add-on service" },
              { title: "SaaS Platforms", desc: "Embed AI features into your existing product" },
              { title: "Consultants", desc: "Deliver AI-powered reports and analysis" },
              { title: "Web Agencies", desc: "Build AI chatbots and tools for clients" }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-start gap-4 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6"
              >
                <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-zinc-400 text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Waitlist */}
      <section className="py-20 px-6 bg-zinc-900/50">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {submitted ? "You're on the list!" : "Get Early Access"}
          </h2>
          
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-8"
            >
              <CheckCircle className="w-16 h-16 text-blue-400 mx-auto mb-4" />
              <p className="text-zinc-300">
                We'll reach out within 48 hours to discuss your white-label setup.
              </p>
            </motion.div>
          ) : (
            <>
              <p className="text-zinc-400 mb-8">
                Join the waitlist. Limited spots for launch partners.
              </p>
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="you@agency.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-zinc-800 border-zinc-700 text-white h-14 rounded-full px-6"
                  required
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-white font-bold rounded-full h-14 px-8"
                >
                  {loading ? "Joining..." : "Join Waitlist"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
